import { Box, Card, CardContent, Skeleton, Stack, Typography } from '@mui/joy';
import { ChartOptions } from 'chart.js';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { VerticalBarChart } from 'reports/components/charts';
import {
  QuotesDashboardDateRange,
  QuotesDashboardQuoteType,
  QuotesVsOrdersView,
} from 'reports/components/QuotesDashboard';
import { IQuotesDashboardDetails } from 'reports/types';
import { Direction, Level, SkeletonVariant, Variant } from 'theme/enums';

import { useChartData } from './useChartData';
import { ViewToggle } from './ViewToggle';

const CARD_HEIGHT = 480;
const CHART_HEIGHT = 370;

const BAR_GROUP_WIDTH = 30;
const MIN_CHART_WIDTH = 300;

const CHART_COLORS = {
  orders: '#125DCB',
  quotes: '#6FAFFF',
};

interface IProps {
  type: QuotesDashboardQuoteType;
  data?: IQuotesDashboardDetails;
  dateRange: QuotesDashboardDateRange;
  isLoading: boolean;
}

export const QuotesVsOrdersCard = ({ type, data, dateRange, isLoading }: IProps) => {
  const [view, setView] = useState<QuotesVsOrdersView>(QuotesVsOrdersView.QUANTITY);

  const { t } = useTranslation();

  const chartData = useChartData({ data, dateRange, type, view });

  const chartOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      plugins: {
        datalabels: {
          display: false,
        },
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          stacked: false,
        },
        y: {
          beginAtZero: true,
          stacked: false,
        },
      },
    }),
    []
  );

  const chartMinWidth = useMemo(() => {
    const dataPointCount = chartData.labels.length;

    return Math.max(MIN_CHART_WIDTH, dataPointCount * BAR_GROUP_WIDTH);
  }, [chartData.labels.length]);

  if (isLoading) {
    return (
      <Card variant={Variant.OUTLINED} sx={{ height: CARD_HEIGHT }}>
        <CardContent>
          <Stack direction={Direction.ROW} sx={{ justifyContent: 'space-between', mb: 2 }}>
            <Skeleton sx={{ height: 24, width: '40%' }} variant={SkeletonVariant.TEXT} />
            <Skeleton sx={{ height: 32, width: 150 }} variant={SkeletonVariant.RECTANGULAR} />
          </Stack>
          <Skeleton
            sx={{ height: CHART_HEIGHT, width: '100%' }}
            variant={SkeletonVariant.RECTANGULAR}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant={Variant.OUTLINED} sx={{ height: CARD_HEIGHT }}>
      <CardContent>
        <Stack direction={Direction.ROW} sx={{ justifyContent: 'space-between', mb: 2, mt: 1 }}>
          <Typography level={Level.TITLE_MD}>{t('labelQuotesVsOrders')}</Typography>
          <ViewToggle onChange={setView} type={type} value={view} />
        </Stack>
        <Box sx={{ height: CHART_HEIGHT, overflowX: 'auto' }}>
          <Box sx={{ height: '100%', minWidth: chartMinWidth }}>
            <VerticalBarChart data={chartData} height="100%" options={chartOptions} />
          </Box>
        </Box>
        <Stack direction={Direction.ROW} sx={{ gap: 3, justifyContent: 'center', mt: 1 }}>
          <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                backgroundColor: CHART_COLORS.quotes,
                borderRadius: 0.5,
                height: 12,
                width: 12,
              }}
            />
            <Typography level={Level.BODY_SM}>{t('labelQuotes')}</Typography>
          </Stack>
          <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                backgroundColor: CHART_COLORS.orders,
                borderRadius: 0.5,
                height: 12,
                width: 12,
              }}
            />
            <Typography level={Level.BODY_SM}>{t('labelOrders')}</Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
